import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type PropsWithChildren,
} from "react"

import {
  getHistoryNavigationDirection,
  type PageTransitionDirection,
} from "@be-miniapp/ui/utils/page-transition"

interface AnimationState {
  animate: boolean
  direction: PageTransitionDirection
}

interface VersionedAnimationState extends AnimationState {
  version: number
}

interface RouteTransitionElements {
  pageElement: HTMLElement | null
  snapshotLayerElement: HTMLElement | null
}

interface AnimationRouterContextValue {
  setAnimate: (state: AnimationState) => void
  prepareNavigation: (state: AnimationState) => void
  markPopstatePrepared: () => void
  getAnimationState: () => VersionedAnimationState
  registerRouteTransitionElements: (
    elements: RouteTransitionElements
  ) => () => void
  clearPreparedSnapshot: (snapshotElement?: HTMLElement | null) => void
  animate: boolean
  direction: PageTransitionDirection
  version: number
}

export const AnimationRouterContext = /*#__PURE__*/ React.createContext<
  AnimationRouterContextValue | undefined
>(undefined)

interface AnimationRouterContextProviderProps extends PropsWithChildren {}

export function AnimationRouterContextProvider({
  children,
}: AnimationRouterContextProviderProps) {
  const [state, setState] = React.useState<VersionedAnimationState>({
    animate: false,
    direction: "forward",
    version: 0,
  })

  const stateRef = useRef(state)
  const pageElementRef = useRef<HTMLElement | null>(null)
  const snapshotLayerElementRef = useRef<HTMLElement | null>(null)
  const preparedSnapshotRef = useRef<HTMLElement | null>(null)
  const lastHistoryIndexRef = useRef<number | null>(readHistoryIndex())
  const skipNextPopstatePrepareRef = useRef(false)

  const handleSetAnimate = useCallback((next: AnimationState) => {
    const direction =
      next.direction === "forward" || next.direction === "backward"
        ? next.direction
        : "forward"

    const value: VersionedAnimationState = {
      animate: !!next.animate,
      direction,
      version: stateRef.current.version + 1,
    }

    stateRef.current = value
    setState(value)
  }, [])

  const clearPreparedSnapshot = useCallback(
    (snapshotElement?: HTMLElement | null) => {
      if (
        snapshotElement &&
        preparedSnapshotRef.current &&
        preparedSnapshotRef.current !== snapshotElement
      ) {
        return
      }

      preparedSnapshotRef.current?.remove()
      preparedSnapshotRef.current = null
    },
    []
  )

  const prepareSnapshot = useCallback(() => {
    const pageElement = pageElementRef.current
    const snapshotLayerElement = snapshotLayerElementRef.current

    if (!pageElement || !snapshotLayerElement) return

    clearPreparedSnapshot()

    const snapshot = pageElement.cloneNode(true) as HTMLElement
    snapshot.setAttribute("aria-hidden", "true")
    snapshot.setAttribute("inert", "")
    snapshot.dataset.pageTransitionSnapshot = "true"

    copyScrollPositions(pageElement, snapshot)
    copyCanvasContent(pageElement, snapshot)

    Object.assign(snapshot.style, {
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      margin: "0",
      overflow: "hidden",
      pointerEvents: "none",
      transform: "translate3d(0, 0, 0)",
      opacity: "1",
      transition: "none",
      background: "var(--be-page-transition-background, #f2f3f4)",
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden",
      WebkitTransform: "translate3d(0, 0, 0)",
      willChange: "transform",
      contain: "paint",
    })

    snapshotLayerElement.appendChild(snapshot)
    preparedSnapshotRef.current = snapshot
  }, [clearPreparedSnapshot])

  const markPopstatePrepared = useCallback(() => {
    skipNextPopstatePrepareRef.current = true
  }, [])

  const prepareNavigation = useCallback(
    (next: AnimationState) => {
      handleSetAnimate(next)

      if (next.animate) {
        prepareSnapshot()
      } else {
        clearPreparedSnapshot()
      }
    },
    [clearPreparedSnapshot, handleSetAnimate, prepareSnapshot]
  )

  const getAnimationState = useCallback(() => stateRef.current, [])

  const registerRouteTransitionElements = useCallback(
    ({ pageElement, snapshotLayerElement }: RouteTransitionElements) => {
      pageElementRef.current = pageElement
      snapshotLayerElementRef.current = snapshotLayerElement

      return () => {
        if (pageElementRef.current === pageElement) {
          pageElementRef.current = null
        }
        if (snapshotLayerElementRef.current === snapshotLayerElement) {
          snapshotLayerElementRef.current = null
        }
      }
    },
    []
  )

  useEffect(() => {
    lastHistoryIndexRef.current = readHistoryIndex()

    const handlePopState = () => {
      const nextIndex = readHistoryIndex()
      const direction = getHistoryNavigationDirection({
        previousIndex: lastHistoryIndexRef.current,
        nextIndex,
        fallback: "backward",
      })

      lastHistoryIndexRef.current = nextIndex

      if (skipNextPopstatePrepareRef.current) {
        skipNextPopstatePrepareRef.current = false
        return
      }

      prepareNavigation({ animate: true, direction })
    }

    window.addEventListener("popstate", handlePopState, { capture: true })
    return () => {
      window.removeEventListener("popstate", handlePopState, { capture: true })
    }
  }, [prepareNavigation])

  const contextValue = useMemo<AnimationRouterContextValue>(
    () => ({
      setAnimate: handleSetAnimate,
      prepareNavigation,
      markPopstatePrepared,
      getAnimationState,
      registerRouteTransitionElements,
      clearPreparedSnapshot,
      animate: state.animate,
      direction: state.direction,
      version: state.version,
    }),
    [
      state.animate,
      state.direction,
      state.version,
      handleSetAnimate,
      prepareNavigation,
      markPopstatePrepared,
      getAnimationState,
      registerRouteTransitionElements,
      clearPreparedSnapshot,
    ]
  )

  return (
    <AnimationRouterContext.Provider value={contextValue}>
      {children}
    </AnimationRouterContext.Provider>
  )
}

function readHistoryIndex(): number | null {
  if (typeof window === "undefined") return null

  const index = window.history.state?.idx
  return typeof index === "number" ? index : null
}

function copyScrollPositions(source: HTMLElement, snapshot: HTMLElement) {
  snapshot.scrollTop = source.scrollTop
  snapshot.scrollLeft = source.scrollLeft
}

function copyCanvasContent(source: HTMLElement, snapshot: HTMLElement) {
  const sourceCanvases = source.querySelectorAll("canvas")
  const snapshotCanvases = snapshot.querySelectorAll("canvas")

  sourceCanvases.forEach((sourceCanvas, index) => {
    const snapshotCanvas = snapshotCanvases[index]
    if (!snapshotCanvas) return

    snapshotCanvas.width = sourceCanvas.width
    snapshotCanvas.height = sourceCanvas.height

    try {
      snapshotCanvas
        .getContext("2d")
        ?.drawImage(sourceCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height)
    } catch {
      // WebGL/tainted canvases are skipped; surrounding layout is still preserved.
    }
  })
}
